// Stored files (plan 3D, F1). What can be stored is decided here, from the bytes:
// the declared type and the name are never trusted.

export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export type ImageType = (typeof IMAGE_TYPES)[number];

/** Under Discord's 10 MB limit for a server with no boosts, and Caddy's 10 MiB `request_body` limit (deploy/caddy/itcweb.caddy). */
export const MAX_FILE_BYTES = 8 * 1024 * 1024;

/** The longest stored name, its extension included. */
export const MAX_FILE_NAME = 100;

/** Carries the upload's name, URI-encoded: the body is the file's bytes, so there's nowhere else to put it. */
export const FILE_NAME_HEADER = 'x-file-name';

const EXTENSIONS: Readonly<Record<ImageType, string>> = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp' };

const PNG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG = [0xff, 0xd8, 0xff];
const RIFF = [0x52, 0x49, 0x46, 0x46];
const WEBP = [0x57, 0x45, 0x42, 0x50];

function startsWith(bytes: Uint8Array, signature: readonly number[], offset = 0): boolean {
  return signature.every((byte, index) => bytes[offset + index] === byte);
}

/** The image type the bytes' signature says, or null for anything else, SVG included. */
export function sniffImage(bytes: Uint8Array): ImageType | null {
  if (startsWith(bytes, PNG)) return 'image/png';
  if (startsWith(bytes, JPEG)) return 'image/jpeg';
  if (startsWith(bytes, RIFF) && startsWith(bytes, WEBP, 8)) return 'image/webp';
  return null;
}

/** A name for display and for Discord: `[A-Za-z0-9._-]` only, with the extension of the sniffed type. */
export function safeFileName(name: string, mime: ImageType): string {
  const extension = `.${EXTENSIONS[mime]}`;
  const base = name.split(/[/\\]/).at(-1) ?? '';
  const stem = base
    .replace(/\.[^.]*$/, '')
    .replace(/[^A-Za-z0-9._-]/g, '')
    .replace(/^\.+/, '')
    .slice(0, MAX_FILE_NAME - extension.length);
  return `${stem === '' ? 'file' : stem}${extension}`;
}
